import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';

export interface UseCesiumCameraArgs {
  isViewerReady: boolean;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  defaultMapPositions: any;
  boundingLayers: any;
  mapId?: string | number;
}

export const useCesiumCamera = ({ isViewerReady, viewerRef, defaultMapPositions, boundingLayers, mapId }: UseCesiumCameraArgs) => {
  // Tracks which map the camera was positioned for; the viewer outlives a map switch.
  const positionedMapId = useRef<string | number | undefined>(undefined);

  // 2a. Handle Initial Camera Position (once per map)
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current || !defaultMapPositions) return;
    if (positionedMapId.current === mapId) return;

    const viewer = viewerRef.current;
    const coords = defaultMapPositions?.defaultMapPosition?.coordinates;

    // Without coordinates the map's settings are still loading, so leave the camera alone.
    if (coords && coords.lat !== undefined && coords.lng !== undefined) {
      viewer.scene.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(coords.lng, coords.lat, coords.zoom || 25000000),
      });
      positionedMapId.current = mapId;
    }
  }, [isViewerReady, defaultMapPositions, mapId]);

  // 2b. Handle Zoom to Layer (Runs whenever boundingLayers changes)
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current || !boundingLayers) return;

    const b = boundingLayers;
    const hasData = (b.minX || b.minx) && (b.minY || b.miny);

    if (hasData) {
      const viewer = viewerRef.current;
      const minX = parseFloat(b.minX || b.minx);
      const minY = parseFloat(b.minY || b.miny);
      const maxX = parseFloat(b.maxX || b.maxx);
      const maxY = parseFloat(b.maxY || b.maxy);

      const rectangle = Cesium.Rectangle.fromDegrees(minX, minY, maxX, maxY);

      viewer.camera.flyTo({
        destination: rectangle,
        duration: 1.5,
      });
    }
  }, [boundingLayers, isViewerReady]);
};
